import { useI18n, type CommonKey } from "@hive/i18n";
import { Select } from "./select";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
  type ReactNode,
} from "react";
import { Icon } from "./icons";
import { cx } from "./layout";

/**
 * Campos de formulário acessíveis: rótulo ligado ao campo, dica e erro em aria-describedby,
 * aria-invalid quando há erro. A dica (`hint`) também alimenta o modo "O que é isto?".
 */
export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  /** Texto de ajuda do campo para o "O que é isto?" (quando difere da dica visível). */
  help?: string;
}

function useFieldIds(error?: ReactNode, hint?: ReactNode) {
  const id = useId();
  const hintId = hint ? `${id}-h` : undefined;
  const errId = error ? `${id}-e` : undefined;
  return {
    id,
    describedBy: [errId, hintId].filter(Boolean).join(" ") || undefined,
    hintId,
    errId,
  };
}

function FieldFrame({
  id,
  label,
  hint,
  error,
  required,
  className,
  help,
  hintId,
  errId,
  children,
}: FieldProps & {
  id: string;
  hintId?: string;
  errId?: string;
  children: ReactNode;
}) {
  const { t } = useI18n<CommonKey>();
  return (
    <div className={cx("field", className)} data-help={help}>
      <label htmlFor={id}>
        {label}
        {required ? (
          <span className="sr-only"> ({t("ui.required")})</span>
        ) : null}
      </label>
      {children}
      {error && (
        <span className="field-err" id={errId}>
          <Icon name="alert" size="sm" />
          {error}
        </span>
      )}
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

export function TextField({
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  suffix,
  readOnly,
  maxLength,
  inputMode,
  autoFocus,
  ...f
}: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "url" | "number" | "search" | "password";
  placeholder?: string;
  autoComplete?: string;
  suffix?: ReactNode;
  readOnly?: boolean;
  maxLength?: number;
  inputMode?: "text" | "email" | "url" | "numeric";
  autoFocus?: boolean;
}) {
  const ids = useFieldIds(f.error, f.hint);
  const input = (
    <input
      id={ids.id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={f.error ? true : undefined}
      aria-describedby={ids.describedBy}
      aria-required={f.required || undefined}
      readOnly={readOnly}
      maxLength={maxLength}
      inputMode={inputMode}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- foco no primeiro campo de diálogos curtos
      autoFocus={autoFocus}
      style={suffix ? { flex: 1, minWidth: 0 } : undefined}
    />
  );
  return (
    <FieldFrame {...f} {...ids}>
      {suffix ? (
        <div className="row" style={{ flexWrap: "nowrap", gap: 6 }}>
          {input}
          <span className="mono small muted nowrap">{suffix}</span>
        </div>
      ) : (
        input
      )}
    </FieldFrame>
  );
}

export function TextAreaField({
  value,
  onChange,
  rows = 4,
  placeholder,
  maxLength,
  ...f
}: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
}) {
  const ids = useFieldIds(f.error, f.hint);
  return (
    <FieldFrame {...f} {...ids}>
      <textarea
        id={ids.id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={f.error ? true : undefined}
        aria-describedby={ids.describedBy}
        aria-required={f.required || undefined}
      />
    </FieldFrame>
  );
}

export interface Option {
  value: string;
  label: string;
  desc?: ReactNode;
  disabled?: boolean;
  group?: string;
}

export function SelectField({
  value,
  onChange,
  options,
  ...f
}: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
}) {
  const ids = useFieldIds(f.error, f.hint);
  return (
    <FieldFrame {...f} {...ids}>
      <Select
        id={ids.id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={f.error ? true : undefined}
        aria-describedby={ids.describedBy}
        aria-required={f.required || undefined}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </Select>
    </FieldFrame>
  );
}

/** Segredo (senha, token, chave): nunca é preenchido de volta; mostrar/ocultar enquanto digita. */
export function SecretField({
  value,
  onChange,
  keepHint,
  ...f
}: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  keepHint?: boolean;
}) {
  const { t } = useI18n<CommonKey>();
  const [show, setShow] = useState(false);
  const hint = f.hint ?? (keepHint ? t("wz.secretKeep") : t("wz.secretHint"));
  const ids = useFieldIds(f.error, hint);
  return (
    <FieldFrame {...f} hint={hint} {...ids}>
      <div className="row" style={{ flexWrap: "nowrap", gap: 6 }}>
        <input
          id={ids.id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          placeholder={keepHint ? "••••••••" : undefined}
          aria-invalid={f.error ? true : undefined}
          aria-describedby={ids.describedBy}
          aria-required={f.required || undefined}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setShow((v) => !v)}
          aria-pressed={show}
        >
          <Icon name="eye" size="sm" />
          {show ? t("ui.hide") : t("ui.show")}
        </button>
      </div>
    </FieldFrame>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <div
      className="stack-sm"
      style={{ gap: 2 }}
      data-help={typeof hint === "string" ? hint : undefined}
    >
      <label className="switch" htmlFor={id}>
        <input
          type="checkbox"
          role="switch"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-describedby={hint ? `${id}-h` : undefined}
        />
        <span>{label}</span>
      </label>
      {hint && (
        <span className="hint" id={`${id}-h`} style={{ paddingLeft: 42 }}>
          {hint}
        </span>
      )}
    </div>
  );
}

/** Cartões de opção com rádio (uma escolha), como no protótipo. */
export function RadioCards({
  legend,
  value,
  onChange,
  options,
  cols1,
  error,
}: {
  legend: ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  cols1?: boolean;
  error?: ReactNode;
}) {
  const name = useId();
  return (
    <fieldset aria-invalid={error ? true : undefined}>
      <legend>{legend}</legend>
      <div className={cx("opts", cols1 && "cols-1")} style={{ marginTop: 6 }}>
        {options.map((o) => (
          <label className="opt" key={o.value}>
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              disabled={o.disabled}
              onChange={() => onChange(o.value)}
            />
            <span className="opt-title">{o.label}</span>
            {o.desc && <span className="opt-desc">{o.desc}</span>}
          </label>
        ))}
      </div>
      {error && (
        <span className="field-err" style={{ marginTop: 6 }}>
          <Icon name="alert" size="sm" />
          {error}
        </span>
      )}
    </fieldset>
  );
}

/**
 * Cartões de opção com caixa de seleção (várias escolhas). Com `group` nas opções,
 * agrupa por área e oferece marcar/desmarcar a área inteira.
 */
export function CheckCards({
  legend,
  value,
  onChange,
  options,
  error,
  groupLabel,
  hint,
}: {
  legend: ReactNode;
  value: string[];
  onChange: (v: string[]) => void;
  options: Option[];
  error?: ReactNode;
  groupLabel?: (g: string) => string;
  hint?: ReactNode;
}) {
  const { t } = useI18n<CommonKey>();
  const set = new Set(value);
  const toggle = (v: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(v);
    else next.delete(v);
    onChange(options.map((o) => o.value).filter((x) => next.has(x)));
  };
  const groups = new Map<string, Option[]>();
  for (const o of options) {
    const g = o.group ?? "";
    groups.set(g, [...(groups.get(g) ?? []), o]);
  }
  const grid = (opts: Option[]) => (
    <div className="opts" style={{ marginTop: 6 }}>
      {opts.map((o) => (
        <label className="opt" key={o.value}>
          <input
            type="checkbox"
            value={o.value}
            checked={set.has(o.value)}
            disabled={o.disabled}
            onChange={(e) => toggle(o.value, e.target.checked)}
          />
          <span className="opt-title">{o.label}</span>
          {o.desc && <span className="opt-desc">{o.desc}</span>}
        </label>
      ))}
    </div>
  );
  return (
    <fieldset aria-invalid={error ? true : undefined}>
      <legend>
        {legend}{" "}
        <span className="muted small">
          · {t("wz.selected", { count: value.length })}
        </span>
      </legend>
      {hint && <p className="hint">{hint}</p>}
      {options.length === 0 && (
        <p className="small muted">{t("wz.noOptions")}</p>
      )}
      {[...groups.entries()].map(([g, opts]) =>
        g ? (
          <div key={g} style={{ marginTop: 10 }}>
            <div className="row between">
              <b className="small">{groupLabel ? groupLabel(g) : g}</b>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  const all = opts.every((o) => set.has(o.value));
                  const next = new Set(set);
                  for (const o of opts) {
                    if (all) next.delete(o.value);
                    else next.add(o.value);
                  }
                  onChange(
                    options.map((o) => o.value).filter((x) => next.has(x)),
                  );
                }}
              >
                {opts.every((o) => set.has(o.value))
                  ? t("wz.clearArea", { area: groupLabel ? groupLabel(g) : g })
                  : t("wz.selectArea", {
                      area: groupLabel ? groupLabel(g) : g,
                    })}
              </button>
            </div>
            {grid(opts)}
          </div>
        ) : (
          <div key="_">{grid(opts)}</div>
        ),
      )}
      {error && (
        <span className="field-err" style={{ marginTop: 6 }}>
          <Icon name="alert" size="sm" />
          {error}
        </span>
      )}
    </fieldset>
  );
}

/** Lista de etiquetas (sites, tags, grupos): digita e Enter inclui; cada uma tem botão de remover. */
/** Uma tag que já existe, com quantos ativos a usam (0 = valor do dicionário de tags ainda sem uso). */
export type TagSuggestion = { tag: string; assets: number };

/**
 * Quem busca as tags existentes enquanto a pessoa digita num TagsField (a console liga em GET /api/v1/tags/suggest).
 * Sem provedor, o campo continua só digitando.
 */
export const TagSuggestContext = createContext<
  ((q: string) => Promise<TagSuggestion[]>) | null
>(null);

export function TagsField({
  value,
  onChange,
  placeholder,
  suggest = true,
  ...f
}: FieldProps & {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  suggest?: boolean;
}) {
  const { t } = useI18n<CommonKey>();
  const [draft, setDraft] = useState("");
  const [found, setFound] = useState<TagSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const finder = useContext(TagSuggestContext);
  const hint = f.hint ?? t("wz.tagsHint");
  const ids = useFieldIds(f.error, hint);
  const listId = `${ids.id}-sug`;
  const on = suggest && !!finder;
  // busca as tags existentes depois de uma pausa na digitação (2 letras ou mais), sem as já escolhidas
  useEffect(() => {
    const q = draft.trim();
    if (!on || q.length < 2) {
      setFound([]);
      return;
    }
    let alive = true;
    const h = window.setTimeout(() => {
      void finder(q)
        .then((r) => {
          if (alive) {
            setFound(r.filter((x) => !value.includes(x.tag)).slice(0, 8));
            setActive(-1);
          }
        })
        .catch(() => alive && setFound([]));
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(h);
    };
  }, [draft, on, finder, value]);
  const add = (raw?: string) => {
    const v = (raw ?? draft).trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
    setFound([]);
  };
  const open = found.length > 0;
  return (
    <FieldFrame {...f} hint={hint} {...ids}>
      <div
        className="row"
        style={{ flexWrap: "nowrap", gap: 6, position: "relative" }}
      >
        <input
          id={ids.id}
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => window.setTimeout(() => setFound([]), 150)}
          onKeyDown={(e) => {
            if (open && e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => (i + 1) % found.length);
            } else if (open && e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => (i <= 0 ? found.length - 1 : i - 1));
            } else if (e.key === "Escape") {
              setFound([]);
            } else if (e.key === "Enter") {
              e.preventDefault();
              add(open && active >= 0 ? found.at(active)?.tag : undefined);
            }
          }}
          role={on ? "combobox" : undefined}
          aria-expanded={on ? open : undefined}
          aria-controls={on ? listId : undefined}
          aria-autocomplete={on ? "list" : undefined}
          aria-activedescendant={
            open && active >= 0 ? `${listId}-${active}` : undefined
          }
          aria-describedby={ids.describedBy}
          aria-invalid={f.error ? true : undefined}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => add()}
          disabled={!draft.trim()}
        >
          <Icon name="plus" size="sm" />
          {t("wz.tagsAdd")}
        </button>
        {open && (
          <ul
            className="tag-sug"
            id={listId}
            role="listbox"
            aria-label={t("wz.tagsExisting")}
          >
            {found.map((x, i) => (
              <li
                key={x.tag}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? "on" : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(x.tag);
                }}
              >
                <span>{x.tag}</span>
                <span className="muted xs">
                  {x.assets > 0
                    ? t("wz.tagsUsed", { n: x.assets })
                    : t("wz.tagsDictionary")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {value.length > 0 && (
        <div className="tags" style={{ marginTop: 4 }}>
          {value.map((v) => (
            <span className="chip" key={v}>
              {v}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== v))}
                aria-label={t("wz.tagsRemove", { value: v })}
              >
                <Icon name="x" size="sm" />
              </button>
            </span>
          ))}
        </div>
      )}
    </FieldFrame>
  );
}
